import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Building2, RefreshCw, AlertTriangle, Search,
} from "lucide-react";
import { toast } from "sonner";
import { PageHero, KpiCard, Section, GridSkeleton, StatusBadge } from "@/components/AnalyticsCommon";

export default function SectorIndicatorsPage() {
  const { companyParam, companies, selected, select } = useCompany();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showRiskOnly, setShowRiskOnly] = useState(false);
  const [sortBy, setSortBy] = useState("headcount");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (companyParam) params.company_id = companyParam;
      const { data: d } = await api.get("/performance/setores", { params });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [companyParam]);

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = data.sectors;
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter((s) => (s.sector_name || "").toLowerCase().includes(q));
    }
    if (showRiskOnly) rows = rows.filter((s) => s.risco_alto);
    rows = [...rows].sort((a, b) => {
      const av = a[sortBy] ?? -1;
      const bv = b[sortBy] ?? -1;
      return bv - av;
    });
    return rows;
  }, [data, search, showRiskOnly, sortBy]);

  return (
    <div className="space-y-5 pb-10" data-testid="sectors-perf-page">
      <PageHero
        kicker={<><Building2 className="inline h-3 w-3 mr-1.5 -mt-0.5" /> Performance & Analytics</>}
        title="Indicadores por Setor"
        description="Visão consolidada de cada setor — headcount, turnover, absenteísmo, performance e avaliações."
        actions={
          <>
            <Select value={selected || "all"} onValueChange={select}>
              <SelectTrigger className="h-10 w-full sm:w-[220px] border-white/20 bg-white/10 text-white" data-testid="sec-company">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas (Grupo)</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={load}
                    className="h-10 border-white/20 bg-white/5 text-white hover:bg-white/15"
                    data-testid="sec-refresh">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </>
        }
      />

      {loading ? <GridSkeleton rows={3} /> : data && (
        <>
          <div className="grid grid-cols-3 gap-3" data-testid="sec-summary">
            <KpiCard label="Total de setores" value={data.summary.total_setores} accent="emerald" testId="sec-total" />
            <KpiCard label="Headcount total" value={data.summary.headcount_total} accent="ochre" testId="sec-hc" />
            <KpiCard label="Setores em risco" value={data.summary.setores_em_risco}
                    sub="Turnover ≥ 15% ou perf < 3" accent={data.summary.setores_em_risco > 0 ? "rose" : "emerald"}
                    testId="sec-risk" />
          </div>

          <Section kicker="Setores" title="Indicadores consolidados" testId="sec-table"
                   action={
                     <div className="flex items-center gap-2 flex-wrap">
                       <div className="relative">
                         <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                         <Input value={search} onChange={(e) => setSearch(e.target.value)}
                                placeholder="Buscar setor..." className="h-9 pl-8 w-[200px]"
                                data-testid="sec-search" />
                       </div>
                       <Select value={sortBy} onValueChange={setSortBy}>
                         <SelectTrigger className="h-9 w-[170px]" data-testid="sec-sort"><SelectValue /></SelectTrigger>
                         <SelectContent>
                           <SelectItem value="headcount">Por Headcount</SelectItem>
                           <SelectItem value="turnover_pct">Por Turnover</SelectItem>
                           <SelectItem value="media_desempenho">Por Desempenho</SelectItem>
                           <SelectItem value="avaliacoes_pct">Por % Aval. concluídas</SelectItem>
                         </SelectContent>
                       </Select>
                       <Button size="sm" variant={showRiskOnly ? "default" : "outline"}
                               onClick={() => setShowRiskOnly(!showRiskOnly)}
                               className={showRiskOnly ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}
                               data-testid="sec-risk-toggle">
                         <AlertTriangle className="h-3 w-3 mr-1" /> Risco
                       </Button>
                     </div>
                   }>
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="bg-[#234935] text-white">
                    <th className="text-left px-3 py-2 font-semibold sticky left-0 bg-[#234935]">Setor</th>
                    <th className="text-right px-3 py-2 font-semibold">Headcount</th>
                    <th className="text-right px-3 py-2 font-semibold">Aprovado</th>
                    <th className="text-right px-3 py-2 font-semibold">Adm. (90d)</th>
                    <th className="text-right px-3 py-2 font-semibold">Desl. (90d)</th>
                    <th className="text-right px-3 py-2 font-semibold">Turnover</th>
                    <th className="text-right px-3 py-2 font-semibold">Absenteísmo</th>
                    <th className="text-right px-3 py-2 font-semibold">Desempenho</th>
                    <th className="text-right px-3 py-2 font-semibold">Aval. %</th>
                    <th className="text-center px-3 py-2 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={10} className="px-3 py-8 text-center text-muted-foreground italic">Nenhum setor encontrado.</td></tr>
                  ) : filtered.map((s, idx) => (
                    <tr key={s.sector_id} className={idx % 2 ? "bg-slate-50/50" : ""}>
                      <td className={`px-3 py-2 font-medium sticky left-0 ${idx % 2 ? "bg-slate-50/50" : "bg-white"}`}>
                        {s.sector_name}
                        {s.responsavel && <div className="text-[10px] text-muted-foreground">{s.responsavel}</div>}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{s.headcount}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{s.quadro_aprovado}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700">+{s.admitidos_periodo}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-rose-700">-{s.desligados_periodo}</td>
                      <td className={`px-3 py-2 text-right tabular-nums font-semibold ${
                        s.turnover_pct >= 15 ? "text-rose-700" : s.turnover_pct >= 8 ? "text-amber-700" : "text-emerald-700"
                      }`}>{s.turnover_pct}%</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {s.absenteismo_pct === null ? <span className="text-muted-foreground">—</span> : `${s.absenteismo_pct}%`}
                      </td>
                      <td className={`px-3 py-2 text-right tabular-nums font-semibold ${
                        s.media_desempenho === null ? "text-muted-foreground"
                        : s.media_desempenho >= 4 ? "text-emerald-700"
                        : s.media_desempenho >= 3 ? "text-amber-700" : "text-rose-700"
                      }`}>
                        {s.media_desempenho ?? "—"}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{s.avaliacoes_pct}%</td>
                      <td className="px-3 py-2 text-center">
                        {s.risco_alto
                          ? <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200">Risco</Badge>
                          : <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">OK</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </>
      )}
    </div>
  );
}
